
import React from 'react';
import { Home, Package, FileText, Settings, LogOut } from 'lucide-react';
import { Link, useLocation, Navigate } from 'react-router-dom';
import { useKeycloakContext } from '@/contexts/KeycloakContext';
import { useUserRole } from '@/hooks/useUserRole';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';

const AdminLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const { signOut } = useKeycloakContext();
  const { role, isLoading } = useUserRole();
  
  const menuItems = [
    { icon: Home, title: 'Dashboard', path: '/admin' },
    { icon: Package, title: 'Products', path: '/admin/products' },
    { icon: FileText, title: 'Blog Posts', path: '/admin/blog' },
    { icon: Settings, title: 'SEO Settings', path: '/admin/seo' },
  ];

  // Show loading state while checking role
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // For development, we'll allow access to admin panel regardless of role
  // Uncomment this check in production
  /*
  // Redirect non-admin users
  if (role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  */

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full bg-gray-100 dark:bg-gray-900">
        <Sidebar>
          <SidebarHeader>
            <div className="p-2 flex items-center gap-2">
              <div className="flex items-center justify-center w-8 h-8 rounded-md bg-primary text-white font-bold">
                N
              </div>
              <span className="font-bold text-lg">NISOO Admin</span>
            </div>
          </SidebarHeader>

          <SidebarContent>
            <SidebarMenu>
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton 
                    asChild 
                    isActive={
                      item.path.includes('?') 
                        ? location.pathname + location.search === item.path
                        : location.pathname === item.path
                    }
                    tooltip={item.title}
                  >
                    <Link to={item.path}>
                      <item.icon className="h-5 w-5" />
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarContent>

          <SidebarFooter>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton tooltip="Logout" onClick={signOut}>
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarFooter>
        </Sidebar>
        
        <main className="flex-1 overflow-auto">
          <div className="max-w-7xl mx-auto p-6 pb-32">
            {children}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default AdminLayout;
